import { useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api'
import { albumPracticePath } from '../lib/practiceRoute'
import type { PublicAlbum } from '../types'
import { PracticePlayer } from './PracticePlayer'
import { userMessageForError } from '../lib/userError'
import { useI18n } from '../i18n/useI18n'

interface Props {
  albumToken: string
  initialShareId: string | null
  onBack: () => void
}

function albumLocation(albumToken: string, shareId: string): string {
  return albumPracticePath(albumToken, shareId)
}

export function AlbumPractice({ albumToken, initialShareId, onBack }: Props) {
  const { t } = useI18n()
  const [album, setAlbum] = useState<PublicAlbum | null>(null)
  const [shareId, setShareId] = useState<string | null>(null)
  const [message, setMessage] = useState(t('albumPractice.loading'))

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const loaded = await api.getSharedAlbum(albumToken)
        if (cancelled) return
        setAlbum(loaded)
        const available = loaded.songs.filter((song) => song.available)
        const requested = available.find((song) => song.shareId === initialShareId)
        const first = requested ?? available[0]
        if (!first) {
          setMessage(t('albumPractice.noAvailable'))
          return
        }
        setShareId(first.shareId)
        history.replaceState({}, '', albumLocation(loaded.shareToken, first.shareId))
        setMessage('')
      } catch (error) {
        if (cancelled) return
        setMessage(userMessageForError(error, t('albumPractice.error.load')))
      }
    }
    void load()
    return () => { cancelled = true }
  }, [albumToken, initialShareId])

  const selectedSong = useMemo(
    () => album?.songs.find((song) => song.shareId === shareId) ?? null,
    [album, shareId],
  )

  function selectSong(nextShareId: string) {
    const target = album?.songs.find((song) => song.shareId === nextShareId && song.available)
    if (!target || !album) return
    setShareId(target.shareId)
    history.replaceState({}, '', albumLocation(album.shareToken, target.shareId))
  }

  if (album && album.songs.every((song) => !song.available)) {
    return (
      <main className="practice-page">
        <section className="card">
          <p className="eyebrow">{t('albumPractice.eyebrow')}</p>
          <h1>{album.title}</h1>
          <p className="notice">{t('albumPractice.noAvailable')}</p>
          <p className="muted">
            {t('albumPractice.excludedNotice')}
          </p>
          <p><a className="button secondary" href="/">{t('albumPractice.home')}</a></p>
        </section>
      </main>
    )
  }

  if (!album || !shareId || !selectedSong) {
    return (
      <main className="page">
        <button className="button ghost" onClick={onBack}>← {t('common.home')}</button>
        <section className="card empty-state">{message}</section>
      </main>
    )
  }

  return (
    <PracticePlayer
      key={shareId}
      shareId={shareId}
      onBack={onBack}
      album={album}
      onAlbumSongSelect={selectSong}
    />
  )
}
